// src/components/admin/AdminEvents.js
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { 
  collection, addDoc, getDocs, getDoc, doc, updateDoc, deleteDoc, 
  query, where, orderBy, runTransaction, writeBatch, increment 
} from 'firebase/firestore';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css'; 
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { COLORS } from '../../theme';
import { 
  formatDateToIso, getMemberName, getMemberRole, getUserRank 
} from '../../utils';

const HOURS = [...Array(24).keys()].map(h => ('0' + h).slice(-2));
const MINUTES = ["00", "15", "30", "45"];

const UNIFORM_PRESETS = {
  FP: ['RB黒白', 'RB白', 'RB黒', 'ピンク', 'イエロー'],
  GK: ['RB橙緑', 'RB橙', 'RB緑', 'グレー', 'ブルー']
};

const DEFAULT_DESCRIPTION = `・イベント開催の15分前集合
・天候判断等の開催有無は基本2時間前
・開催不可の場合、イベント枠が赤く【中止】となります。
・🎽ユニ色指定をお持ちください。ない方はビブス貸与いたします。
・【🎽RBゲームシャツ】は【アプリSHOP】で購入できます。
・【💴現金支払】は当日、【お支払い後に申請ボタン】をお願いします。

>>プレー中の怪我や紛失等の損害など、補償は一切出来ませんので、各自の自己責任の上、参加のご検討ください。あらかじめスポーツ保険加入をおすすめいたします。`;

const AdminEvents = ({ ranks = [], belongingsMaster = [], users = [] }) => {
    // --- State ---
    const [events, setEvents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [editingId, setEditingId] = useState(null);

    // Form State
    const [title, setTitle] = useState('');
    const [eventType, setEventType] = useState('training');
    const [date, setDate] = useState(formatDateToIso(new Date()));
    const [startHour, setStartHour] = useState('09'); 
    const [startMin, setStartMin] = useState('00');
    const [endHour, setEndHour] = useState('11');     
    const [endMin, setEndMin] = useState('00');
    const [fee, setFee] = useState('1000');           
    const [feePoint, setFeePoint] = useState(''); 
    const [venueOptions, setVenueOptions] = useState([]); 
    const [selectedVenueName, setSelectedVenueName] = useState(''); 
    const [venue, setVenue] = useState(''); 
    const [mapUrl, setMapUrl] = useState('');
    const [paymentUrl, setPaymentUrl] = useState('');
    const [paypayLink, setPaypayLink] = useState(''); 
    const [userPassword, setUserPassword] = useState('');
    const [limitFP, setLimitFP] = useState(30);       
    const [limitGK, setLimitGK] = useState(2);
    const [opponentName, setOpponentName] = useState('');
    const [visitorOpenDelay, setVisitorOpenDelay] = useState('0');
    const [allowedRanks, setAllowedRanks] = useState([]); 
    const [isOfficialEvent, setIsOfficialEvent] = useState(true); 
    const [myUniformFP, setMyUniformFP] = useState('RB黒白');
    const [myUniformGK, setMyUniformGK] = useState('RB橙緑');
    const [isCustomUniformFP, setIsCustomUniformFP] = useState(false);
    const [isCustomUniformGK, setIsCustomUniformGK] = useState(false);
    const [opponentUniformFP, setOpponentUniformFP] = useState('');
    const [opponentUniformGK, setOpponentUniformGK] = useState('');
    const [checkInType, setCheckInType] = useState('team');
    
    const [sendLineNotification, setSendLineNotification] = useState(true);
    const [sendPushNotification, setSendPushNotification] = useState(true);

    // Result & Detail State
    const [homeScore, setHomeScore] = useState('');
    const [awayScore, setAwayScore] = useState('');
    const [mom, setMom] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [matchDetails, setMatchDetails] = useState([]);
    const [isResultFormOpen, setIsResultFormOpen] = useState(false);
    const [momBonus, setMomBonus] = useState(500);
    const [partBonus, setPartBonus] = useState(100);
    const [description, setDescription] = useState(DEFAULT_DESCRIPTION);
    const [selectedBelongings, setSelectedBelongings] = useState([]);

    // CSV Export State
    const [exportYear, setExportYear] = useState(new Date().getFullYear());

    // --- Effects ---
    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        const d = new Date(); d.setFullYear(d.getFullYear() - 1);
        const q = query(collection(db, "events"), where("isoDate", ">=", formatDateToIso(d)), orderBy("isoDate", "asc"));
        const snap = await getDocs(q);
        setEvents(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b)=>(a.isoDate>b.isoDate?1:-1)));
        
        const historyVenues = new Map(); 
        
        snap.docs.forEach(doc => { 
            const e = doc.data(); 
            if(e.venue && e.venue !== 'その他') {
                historyVenues.set(e.venue, e.mapUrl || ''); 
            }
        });
        
        const options = Array.from(historyVenues.entries())
            .map(([name, url]) => ({ name, url }))
            .sort((a, b) => a.name.localeCompare(b.name));

        setVenueOptions(options);
        
        if (!editingId && options.length > 0 && !selectedVenueName) { 
            setSelectedVenueName(options[0].name); 
            setVenue(options[0].name); 
            setMapUrl(options[0].url); 
        } else if (options.length === 0 && !selectedVenueName) {
            setSelectedVenueName('その他');
        }
    };

    // --- Helpers for Stats Processing (Phase 2) ---
    const buildEventStats = (eventData, usersList) => {
        const stats = {};
        const getUserId = (name) => {
            if(!name) return null;
            const u = usersList.find(u => u.displayName === name || u.id === name);
            return u ? u.id : null;
        };
        const add = (uid, field) => {
            if(!uid) return;
            if(!stats[uid]) stats[uid] = {participations:0, goals:0, assists:0, moms:0};
            stats[uid][field] += 1;
        };

        if (eventData.status === 'cancelled') return stats;
        const todayStr = formatDateToIso(new Date());
        if (eventData.isoDate > todayStr) return stats; // 未来のイベントはカウントしない

        (eventData.members || []).forEach(m => {
            const uid = m.userId || getUserId(m.name || m);
            add(uid, 'participations');
        });

        if(eventData.mom) add(getUserId(eventData.mom), 'moms');

        (eventData.matchDetails || []).forEach(md => {
            if(md.scorer && md.scorer !== 'オウンゴール' && md.scorer !== 'その他') add(getUserId(md.scorer), 'goals');
            if(md.assist && md.assist !== 'なし' && md.assist !== 'その他') add(getUserId(md.assist), 'assists');
        });
        return stats;
    };

    // --- Handlers ---

    const resetFormExceptDate = () => { 
        setTitle(''); setStartHour('09'); setStartMin('00'); setEndHour('11'); setEndMin('00'); setFee('1000'); setFeePoint('');
        setPaymentUrl(''); setPaypayLink(''); setUserPassword(''); setLimitFP(30); setLimitGK(2); setOpponentName(''); 
        setMyUniformFP('RB黒白'); setIsCustomUniformFP(false); setMyUniformGK('RB橙緑'); setIsCustomUniformGK(false);
        setOpponentUniformFP(''); setOpponentUniformGK(''); setCheckInType('team'); setVisitorOpenDelay('0');
        setHomeScore(''); setAwayScore(''); setMom(''); setYoutubeUrl(''); setMatchDetails([]); 
        setEditingId(null); setIsResultFormOpen(false); setEventType('training');
        setAllowedRanks([]); setDescription(DEFAULT_DESCRIPTION); setSelectedBelongings([]); 
        setIsOfficialEvent(true);
        
        setSendLineNotification(true);
        setSendPushNotification(true);
        
        if(venueOptions.length>0) { 
            setSelectedVenueName(venueOptions[0].name); 
            setVenue(venueOptions[0].name); 
            setMapUrl(venueOptions[0].url); 
        } else {
            setSelectedVenueName('その他'); 
            setVenue(''); 
            setMapUrl('');
        }
    };

    const handleAddEvent = async (e) => { 
        e.preventDefault(); 
        const openDate = new Date(new Date().getTime() + (parseInt(visitorOpenDelay) * 3600000)).toISOString();
        try { 
            const data = { 
              title, date, time: `${startHour}:${startMin}-${endHour}:${endMin}`, isoDate: date, fee: Number(fee), feePoint: feePoint ? Number(feePoint) : Number(fee),
              venue, mapUrl, paymentUrl, paypayLink, userPassword, limitFP: Number(limitFP), limitGK: Number(limitGK), opponentName, 
              myUniformFP, myUniformGK, opponentUniformFP, opponentUniformGK, checkInType, homeScore, awayScore, mom, youtubeUrl, matchDetails, eventType,
              allowedRanks, description, belongings: selectedBelongings,
              isOfficialEvent: isOfficialEvent,
              sendLineNotification: sendLineNotification,
              sendPushNotification: sendPushNotification,
              notificationTriggeredAt: (sendLineNotification || sendPushNotification) ? new Date().toISOString() : null
            };
            if (!editingId) data.visitorOpenDate = openDate; else if (visitorOpenDelay !== '0') data.visitorOpenDate = openDate;
            
            // ★追加: リアルタイム成績反映（差分計算）ロジック
            const newApplied = buildEventStats(data, users);
            data.appliedStats = newApplied;

            if (editingId) { 
                const oldDoc = await getDoc(doc(db, "events", editingId));
                const oldData = oldDoc.data() || {};
                const oldApplied = oldData.appliedStats || {};

                const batch = writeBatch(db);
                batch.update(doc(db, "events", editingId), data);

                const allUids = new Set([...Object.keys(oldApplied), ...Object.keys(newApplied)]);
                let hasStatsUpdate = false;
                allUids.forEach(uid => {
                    const o = oldApplied[uid] || {participations:0, goals:0, assists:0, moms:0};
                    const n = newApplied[uid] || {participations:0, goals:0, assists:0, moms:0};
                    const dPart = n.participations - o.participations;
                    const dGoal = n.goals - o.goals;
                    const dAst = n.assists - o.assists;
                    const dMom = n.moms - o.moms;
                    
                    if (dPart !== 0 || dGoal !== 0 || dAst !== 0 || dMom !== 0) {
                        hasStatsUpdate = true;
                        batch.update(doc(db, "users", uid), {
                            "stats.participations": increment(dPart),
                            "stats.goals": increment(dGoal),
                            "stats.assists": increment(dAst),
                            "stats.moms": increment(dMom)
                        });
                    }
                });
                await batch.commit();
                toast.success("更新しました (成績も自動計算されました)"); 
            } else { 
                const batch = writeBatch(db);
                const newDocRef = doc(collection(db, "events"));
                batch.set(newDocRef, { ...data, current: 0, members: [], waitlist: [], paidMembers: [], status: 'active', createdAt: new Date().toISOString() });
                
                Object.keys(newApplied).forEach(uid => {
                    const n = newApplied[uid];
                    if (n.participations || n.goals || n.assists || n.moms) {
                        batch.update(doc(db, "users", uid), {
                            "stats.participations": increment(n.participations),
                            "stats.goals": increment(n.goals),
                            "stats.assists": increment(n.assists),
                            "stats.moms": increment(n.moms)
                        });
                    }
                });
                await batch.commit();
                toast.success("作成しました"); 
            }
            resetFormExceptDate(); fetchEvents(); 
        } catch (error) { toast.error("エラー: " + error.message); } 
    };

    const handleEdit = (event) => { 
        setEditingId(event.id); setSelectedDate(new Date(event.isoDate)); setDate(event.isoDate); 
        
        setTitle(event.title); 
        if (event.time && event.time.includes('-')) { const [s, e] = event.time.split('-'); const [sh, sm] = s.split(':'); const [eh, em] = e.split(':'); setStartHour(sh); setStartMin(sm); setEndHour(eh); setEndMin(em); } 
        if (venueOptions.some(v => v.name === event.venue)) { setSelectedVenueName(event.venue); } else { setSelectedVenueName('その他'); }
        setVenue(event.venue || ''); setMapUrl(event.mapUrl || ''); setFee(event.fee); setFeePoint(event.feePoint || event.fee); 
        setPaymentUrl(event.paymentUrl || ''); setPaypayLink(event.paypayLink || ''); setUserPassword(event.userPassword || ''); 
        setLimitFP(event.limitFP); setLimitGK(event.limitGK); setOpponentName(event.opponentName || ''); 
        if(UNIFORM_PRESETS.FP.includes(event.myUniformFP)) { setMyUniformFP(event.myUniformFP); setIsCustomUniformFP(false); } else { setMyUniformFP(event.myUniformFP); setIsCustomUniformFP(true); }
        if(UNIFORM_PRESETS.GK.includes(event.myUniformGK)) { setMyUniformGK(event.myUniformGK); setIsCustomUniformGK(false); } else { setMyUniformGK(event.myUniformGK); setIsCustomUniformGK(true); }
        setOpponentUniformFP(event.opponentUniformFP || ''); setOpponentUniformGK(event.opponentUniformGK || ''); setCheckInType(event.checkInType || 'team'); 
        setHomeScore(event.homeScore || ''); setAwayScore(event.awayScore || ''); setMom(event.mom || ''); setYoutubeUrl(event.youtubeUrl || ''); setMatchDetails(event.matchDetails || []); 
        setVisitorOpenDelay('0'); setEventType(event.eventType || 'training'); 
        setAllowedRanks(event.allowedRanks || []); setDescription(event.description || ''); setSelectedBelongings(event.belongings || []);
        setIsOfficialEvent(event.isOfficialEvent !== false);
        
        setSendLineNotification(false);
        setSendPushNotification(false);

        if (event.homeScore) setIsResultFormOpen(true); 
        window.scrollTo({ top: 0, behavior: 'smooth' }); 
    };

    const handleDelete = async (id) => { 
        if (window.confirm("削除しますか？")) { 
            const oldDoc = await getDoc(doc(db, "events", id));
            const oldData = oldDoc.data() || {};
            const oldApplied = oldData.appliedStats || {};

            const batch = writeBatch(db);
            batch.delete(doc(db, "events", id));
            
            Object.keys(oldApplied).forEach(uid => {
                const o = oldApplied[uid];
                batch.update(doc(db, "users", uid), {
                    "stats.participations": increment(-o.participations),
                    "stats.goals": increment(-o.goals),
                    "stats.assists": increment(-o.assists),
                    "stats.moms": increment(-o.moms)
                });
            });
            await batch.commit();
            fetchEvents(); 
            toast.success("削除しました");
        }
    };
    
    const handleDuplicate = (event) => { 
        if(!window.confirm("コピー作成しますか？")) return; 
        setEditingId(null); 
        handleEdit(event); 
        setEditingId(null); 
        setDate(formatDateToIso(selectedDate)); 
        setHomeScore(''); setAwayScore(''); setMom(''); setYoutubeUrl(''); setMatchDetails([]); setIsResultFormOpen(false); 
        setSendLineNotification(true);
        setSendPushNotification(true);
        window.scrollTo({ top: 0, behavior: 'smooth' }); 
    };

    const togglePayment = async (event, memberName) => {
        const isPaid = event.paidMembers && event.paidMembers.includes(memberName);
        if(!window.confirm(`${memberName}の支払いを変更しますか？`)) return;
        let newPaid = [...(event.paidMembers || [])];
        if(isPaid) newPaid = newPaid.filter(n => n !== memberName); else newPaid.push(memberName);
        await updateDoc(doc(db, "events", event.id), { paidMembers: newPaid }); fetchEvents();
    };

    const toggleCheckInAdmin = async (event, memberName) => {
        const eventRef = doc(db, 'events', event.id);
        const checkInType = event.checkInType || 'team';
        
        try {
            await runTransaction(db, async (transaction) => {
                const sfDoc = await transaction.get(eventRef);
                if (!sfDoc.exists()) throw "Event not found";
                
                const data = sfDoc.data();
                const members = [...(data.members || [])];
                const targetIndex = members.findIndex(m => getMemberName(m) === memberName);
                
                if (targetIndex === -1) return;
                
                const targetMember = { ...members[targetIndex] };
                const isAlreadyCheckedIn = targetMember.checkedInAt;

                if (isAlreadyCheckedIn) {
                    if (!window.confirm(`${memberName} のチェックインを取り消しますか？`)) throw "Cancelled";
                    delete targetMember.checkedInAt;
                    delete targetMember.team;
                    delete targetMember.number;
                } else {
                    if (!window.confirm(`${memberName} を強制チェックインさせますか？`)) throw "Cancelled";
                    targetMember.checkedInAt = new Date().toISOString();
                    
                    if (checkInType === 'team') {
                        const myRole = getMemberRole(targetMember);
                        const checkedInSameRole = members.filter(m => m.checkedInAt && m.team && getMemberRole(m) === myRole);
                        const whiteCount = checkedInSameRole.filter(m => m.team === 'white').length;
                        const blackCount = checkedInSameRole.filter(m => m.team === 'black').length;
                        const nextTeam = whiteCount <= blackCount ? 'white' : 'black';
                        
                        const checkedInTeamMembers = members.filter(m => m.checkedInAt && m.team === nextTeam);
                        const maxNum = checkedInTeamMembers.reduce((max, m) => Math.max(max, m.number || 0), 0);
                        
                        targetMember.team = nextTeam;
                        targetMember.number = maxNum + 1;
                    }
                }
                
                members[targetIndex] = targetMember;
                transaction.update(eventRef, { members: members });
            });
            toast.success("チェックイン状態を更新しました");
            fetchEvents();
        } catch(e) {
            if (e !== "Cancelled") {
                console.error(e);
                toast.error("エラーが発生しました");
            }
        }
    };

    const handleNoShowProcess = async (event) => {
        const noShows = (event.members || []).filter(m => !m.checkedInAt);
        
        if (noShows.length === 0) {
            return Swal.fire({
                title: 'バックレなし！',
                text: '素晴らしい！参加者全員がチェックインを完了しています。',
                icon: 'success',
                confirmButtonColor: '#2e7d32'
            });
        }

        const noShowNames = noShows.map(m => getMemberName(m)).join('<br/>・');

        const { isConfirmed } = await Swal.fire({
            title: '🚨 バックレ（未チェックイン）一覧',
            html: `
                <div style="text-align:left; background:#ffebee; padding:15px; border-radius:8px; border:1px solid #ffcdd2; color:#c62828; font-weight:bold; font-size:14px; line-height:1.5;">
                    ・${noShowNames}
                </div>
                <p style="font-size:12px; color:#666; margin-top:15px; text-align:left;">
                    ※これらのメンバーは参加予定でありながらチェックインをしていません。<br>
                    一括で出場停止（BAN）ペナルティを課しますか？
                </p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: '一括BANを設定する',
            confirmButtonColor: '#d32f2f',
            cancelButtonText: 'やめる'
        });

        if (isConfirmed) {
            const { value: banType } = await Swal.fire({
                title: '出場停止（BAN）設定',
                html: `対象者: <b>${noShows.length}名</b><br>ペナルティ期間を選択してください。`,
                input: 'radio',
                inputOptions: {
                    '7': '7日間 (1週間)',
                    '14': '14日間 (2週間)',
                    'permanent': '無期限 BAN'
                },
                inputValidator: (value) => {
                    if (!value) return '期間を選択してください';
                },
                showCancelButton: true,
                confirmButtonText: '一括BANを実行',
                confirmButtonColor: '#d32f2f'
            });

            if (banType) {
                let banUntil = null;
                if (banType !== 'permanent') {
                    const days = parseInt(banType);
                    const date = new Date();
                    date.setDate(date.getDate() + days);
                    banUntil = date.toISOString();
                }
                
                try {
                    const batch = writeBatch(db);
                    let processedCount = 0;

                    noShows.forEach(m => {
                        const name = getMemberName(m);
                        const userId = m.userId;
                        const targetUser = userId ? users.find(u => u.id === userId) : users.find(u => u.displayName === name);
                        
                        if (targetUser) {
                            const userRef = doc(db, 'users', targetUser.id);
                            batch.update(userRef, { 
                                status: 'banned', 
                                banUntil: banUntil,
                                banReason: `【${event.title} (${event.isoDate})】での無断欠席（未チェックイン）によるペナルティ制限`
                            });
                            processedCount++;
                        }
                    });

                    if (processedCount > 0) {
                        await batch.commit();
                        Swal.fire('成敗完了', `${processedCount}名のバックレユーザーをBANしました。\nBAN一覧タブから確認できます。`, 'success');
                    } else {
                        toast.error('対象ユーザーのデータが見つかりませんでした');
                    }
                } catch(e) {
                    console.error(e);
                    toast.error('エラーが発生しました');
                }
            }
        }
    };

    const handleToggleStatus = async (event) => {
        const newStatus = event.status === 'cancelled' ? 'active' : 'cancelled';
        const actionName = newStatus === 'cancelled' ? '中止' : '再開';
        if(!window.confirm(`このイベントを${actionName}しますか？`)) return;
        try {
            const oldDoc = await getDoc(doc(db, "events", event.id));
            const oldData = oldDoc.data() || {};
            const oldApplied = oldData.appliedStats || {};
            
            const newData = { ...oldData, status: newStatus };
            const newApplied = buildEventStats(newData, users);
            newData.appliedStats = newApplied;

            const batch = writeBatch(db);
            batch.update(doc(db, "events", event.id), { status: newStatus, appliedStats: newApplied });
            
            const allUids = new Set([...Object.keys(oldApplied), ...Object.keys(newApplied)]);
            allUids.forEach(uid => {
                const o = oldApplied[uid] || {participations:0, goals:0, assists:0, moms:0};
                const n = newApplied[uid] || {participations:0, goals:0, assists:0, moms:0};
                const dPart = n.participations - o.participations;
                const dGoal = n.goals - o.goals;
                const dAst = n.assists - o.assists;
                const dMom = n.moms - o.moms;
                
                if (dPart !== 0 || dGoal !== 0 || dAst !== 0 || dMom !== 0) {
                    batch.update(doc(db, "users", uid), {
                        "stats.participations": increment(dPart),
                        "stats.goals": increment(dGoal),
                        "stats.assists": increment(dAst),
                        "stats.moms": increment(dMom)
                    });
                }
            });
            await batch.commit();
            toast.success(`イベントを${actionName}しました`); fetchEvents();
        } catch(e) { toast.error("エラーが発生しました"); }
    };
const handleManageVenues = async () => {
        const currentVenues = venueOptions.map(v => v.name).filter(n => n !== 'その他');
        
        if (currentVenues.length === 0) return toast.error("履歴にある会場がありません");

        const { value: targetVenue } = await Swal.fire({
            title: '会場データの整理',
            text: '編集または削除したい会場を選択してください',
            input: 'select',
            inputOptions: currentVenues.reduce((acc, cur) => ({...acc, [cur]: cur}), {}),
            inputPlaceholder: '会場を選択...',
            showCancelButton: true
        });

        if (!targetVenue) return;

        const { value: action } = await Swal.fire({
            title: `「${targetVenue}」の操作`,
            text: '過去の全イベントに対して実行されます',
            icon: 'question',
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: '✏️ 修正 (名前/URL)',
            denyButtonText: '🗑️ 削除',
            confirmButtonColor: '#2196F3',
            denyButtonColor: '#d33',
            cancelButtonText: 'キャンセル'
        });

        if (!action && action !== false) return; 

        if (action === false) { 
            const confirmDel = await Swal.fire({
                title: '本当に削除しますか？',
                html: `過去のイベントから「${targetVenue}」の情報を消去します。<br>※履歴から消えるため、プルダウンの選択肢にも表示されなくなります。`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'はい、削除します',
                confirmButtonColor: '#d33'
            });

            if (confirmDel.isConfirmed) {
                await executeVenueBatchUpdate(targetVenue, '', '');
            }
            return;
        }

        const currentUrl = venueOptions.find(v => v.name === targetVenue)?.url || '';

        const { value: formValues } = await Swal.fire({
            title: '会場情報の修正',
            html: `
                <div style="text-align:left; font-size:12px; margin-bottom:5px;">会場名</div>
                <input id="swal-venue-name" class="swal2-input" value="${targetVenue}" placeholder="会場名" style="margin-bottom:15px;">
                <div style="text-align:left; font-size:12px; margin-bottom:5px;">GoogleマップURL</div>
                <input id="swal-venue-url" class="swal2-input" value="${currentUrl}" placeholder="https://goo.gl/maps/..." >
            `,
            focusConfirm: false,
            showCancelButton: true,
            preConfirm: () => {
                return [
                    document.getElementById('swal-venue-name').value,
                    document.getElementById('swal-venue-url').value
                ]
            }
        });

        if (formValues) {
            const [newName, newUrl] = formValues;
            if (!newName) return toast.error("会場名は必須です");
            
            if (newName === targetVenue && newUrl === currentUrl) return;

            const confirmEdit = await Swal.fire({
                title: '変更の反映',
                html: `過去のすべての「${targetVenue}」を<br><b>${newName}</b><br>に書き換えますか？`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: '更新する'
            });

            if (confirmEdit.isConfirmed) {
                await executeVenueBatchUpdate(targetVenue, newName, newUrl);
            }
        }
    };

    const executeVenueBatchUpdate = async (targetVenue, newName, newUrl) => {
        try {
            const batch = writeBatch(db);
            const q = query(collection(db, 'events'), where('venue', '==', targetVenue));
            const snapshot = await getDocs(q);

            if (snapshot.empty) {
                toast.error("該当するイベントが見つかりませんでした");
                return;
            }

            snapshot.docs.forEach(doc => {
                batch.update(doc.ref, { 
                    venue: newName, 
                    mapUrl: newUrl 
                });
            });

            await batch.commit();
            
            const actionName = newName ? '更新' : '削除';
            toast.success(`${snapshot.size}件のイベントを${actionName}しました`);
            fetchEvents(); 
            
        } catch(e) {
            console.error(e);
            toast.error("処理中にエラーが発生しました");
        }
    };

    const handleForceRemoveMember = async (event, memberName, isWaitlist) => {
        if (!window.confirm("強制削除しますか？")) return;
        const eventRef = doc(db, 'events', event.id);
        
        try {
            await runTransaction(db, async (t) => {
                const docSnap = await t.get(eventRef);
                if (!docSnap.exists()) throw new Error("Event not found");
                const data = docSnap.data();
                
                let newMembers = [...(data.members || [])];
                let newWaitlist = [...(data.waitlist || [])];
                
                if (isWaitlist) {
                    newWaitlist = newWaitlist.filter(m => getMemberName(m) !== memberName);
                } else {
                    const targetIndex = newMembers.findIndex(m => getMemberName(m) === memberName);
                    if (targetIndex === -1) return;
                    
                    const targetMember = newMembers[targetIndex];
                    const targetRole = getMemberRole(targetMember); 
                    
                    newMembers.splice(targetIndex, 1);
                    
                    const promotedIndex = newWaitlist.findIndex(m => getMemberRole(m) === targetRole);
                    if (promotedIndex !== -1) {
                        const promotedMember = newWaitlist[promotedIndex];
                        newWaitlist.splice(promotedIndex, 1); 
                        newMembers.push(promotedMember); 
                    }
                }
                
                t.update(eventRef, { 
                    members: newMembers, 
                    waitlist: newWaitlist, 
                    current: newMembers.length 
                });
            });
            
            toast.success("削除しました (待機者がいれば繰り上げました)"); 
            fetchEvents(); 
        } catch(e) { 
            console.error(e);
            toast.error("エラーが発生しました"); 
        }
    };

    const handleCancelCheckIn = async (event, memberName) => {
        if (!window.confirm(`${memberName}のチェックイン情報（チーム・背番号）を取り消しますか？\n※参加状態は維持されます。`)) return;
        
        try {
            const eventRef = doc(db, "events", event.id);
            await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(eventRef);
                if (!docSnap.exists()) throw "NoDoc";
                
                const data = docSnap.data();
                const members = [...(data.members || [])];
                const index = members.findIndex(m => getMemberName(m) === memberName);
                
                if (index !== -1) {
                    const target = { ...members[index] };
                    delete target.checkedInAt;
                    delete target.team;
                    delete target.number;
                    
                    members[index] = target;
                    transaction.update(eventRef, { members: members });
                }
            });
            toast.success("チェックインを取り消しました");
            fetchEvents();
        } catch(e) {
            console.error(e);
            toast.error("エラーが発生しました");
        }
    };

    const handleGiveMomBonus = async () => {
        if (!mom) return toast.error("MOMが選択されていません"); if (!editingId) return;
        if (!window.confirm(`${mom}さんに MOMボーナス ${momBonus}P を付与しますか？`)) return;
        const targetUser = users.find(u => u.displayName === mom);
        if (!targetUser) return toast.error(`ユーザー ${mom} が見つかりませんでした`);
        
        try {
            await runTransaction(db, async (t) => {
                const uRef = doc(db, 'users', targetUser.id);
                t.update(uRef, { points: increment(Number(momBonus)) });
                t.set(doc(collection(db, 'point_logs')), { userId: targetUser.id, amount: Number(momBonus), reason: `MOMボーナス: ${title}`, createdAt: new Date().toISOString(), adminAction: true });
                t.set(doc(collection(db, 'accounting')), { date: formatDateToIso(new Date()), type: 'expense', category: 'ポイント発行', amount: Number(momBonus), memo: `${mom}へMOMボーナス (${title})`, createdAt: new Date().toISOString() });
            });
            toast.success('付与しました');
        } catch(e) { toast.error('エラー'); }
    };

    const handleGiveParticipationBonus = async () => {
        if (!editingId) return;
        const editingEvent = events.find(e => e.id === editingId);
        if (!editingEvent || !editingEvent.members) return;
        const members = editingEvent.members;
        if (!window.confirm(`参加者全員(${members.length}名)に ${partBonus}P を付与しますか？`)) return;
        try {
            const batch = writeBatch(db);
            let count = 0; let totalBonus = 0;
            members.forEach(m => {
                const name = getMemberName(m);
                const userId = m.userId;
                const targetUser = userId ? users.find(u => u.id === userId) : users.find(u => u.displayName === name);
                if (targetUser) {
                    const userRef = doc(db, 'users', targetUser.id);
                    batch.update(userRef, { points: increment(Number(partBonus)) });
                    const logRef = doc(collection(db, 'point_logs')); 
                    batch.set(logRef, { userId: targetUser.id, amount: Number(partBonus), reason: `参加還元: ${title}`, eventId: editingId, createdAt: new Date().toISOString(), balanceAfter: -1 });
                    count++; totalBonus += Number(partBonus);
                }
            });
            if (totalBonus > 0) {
               const accRef = doc(collection(db, 'accounting'));
               batch.set(accRef, { date: formatDateToIso(new Date()), type: 'expense', category: '参加還元', amount: totalBonus, memo: `${count}名へ参加還元 (${title})`, createdAt: new Date().toISOString() });
            }
            await batch.commit(); toast.success(`${count}名にコインを付与しました`);
        } catch(e) { toast.error("一括付与に失敗しました"); }
    };

    // ★修正: フェーズ2用 過去データ一括集計＆目印（appliedStats）の付与
    const handleMigrateStats = async () => {
        const confirm1 = await Swal.fire({
            title: '【重要】過去データの一括集計',
            html: '過去の全試合データを読み込み、全メンバーのプロフィールに通算成績を一括で書き込みます。<br>さらに、今後の自動計算のために全イベントに「集計済みマーク」を付与します。<br><br><b>※この処理は導入時に1回だけ実行してください。</b><br>※数分かかることがあります。',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: '実行する',
            confirmButtonColor: '#d32f2f'
        });
        
        if (!confirm1.isConfirmed) return;

        toast.loading("データを集計・紐付け中です...");
        
        try {
            // 1. 最新の全ユーザーを取得
            const usersSnap = await getDocs(collection(db, 'users'));
            const usersList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // 2. 全イベントを取得（キャンセルされたものも一応処理用に回す）
            const eventsSnap = await getDocs(collection(db, 'events'));
            const eventsList = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

            // 3. 集計用の一時的な「ハコ」を用意
            const statsMap = {}; 
            usersList.forEach(u => {
                statsMap[u.id] = { participations: 0, goals: 0, assists: 0, moms: 0 };
            });

            // 4. イベントデータをループして足し算しつつ、イベント自体に「appliedStats」を記録
            let batch = writeBatch(db);
            let count = 0;
            let totalProcessedEvents = 0;
            let totalProcessedUsers = 0;

            eventsList.forEach(ev => {
                const applied = buildEventStats(ev, usersList);
                
                // ユーザーごとの総合成績に合算
                Object.keys(applied).forEach(uid => {
                    if (!statsMap[uid]) statsMap[uid] = { participations: 0, goals: 0, assists: 0, moms: 0 };
                    statsMap[uid].participations += applied[uid].participations || 0;
                    statsMap[uid].goals += applied[uid].goals || 0;
                    statsMap[uid].assists += applied[uid].assists || 0;
                    statsMap[uid].moms += applied[uid].moms || 0;
                });

                // イベントデータ側に「反映済みの成績」として記録
                const eventRef = doc(db, 'events', ev.id);
                batch.update(eventRef, { appliedStats: applied });
                count++;
                totalProcessedEvents++;
            });

            // 5. ユーザーデータをバッチに追加
            for (const uid of Object.keys(statsMap)) {
                const userRef = doc(db, 'users', uid);
                batch.update(userRef, { stats: statsMap[uid] });
                count++;
                totalProcessedUsers++;

                // Firestoreの制限(500件)を回避するため400件ずつコミット
                if (count >= 400) {
                    await batch.commit();
                    batch = writeBatch(db);
                    count = 0;
                }
            }
            
            if (count > 0) {
                await batch.commit();
            }

            toast.dismiss();
            Swal.fire('集計・紐付け完了！', `${totalProcessedEvents}件のイベントと${totalProcessedUsers}名のユーザーデータを連携・更新しました。\nこれで今後の成績は完全自動で反映されます！`, 'success');

        } catch (error) {
            console.error(error);
            toast.dismiss();
            Swal.fire('エラー', '集計中にエラーが発生しました: ' + error.message, 'error');
        }
    };

    // --- CSV Export Logic ---
    const handleExportCSV = async () => {
        try {
            const start = `${exportYear}-01-01`;
            const end = `${exportYear}-12-31`;
            const q = query(
              collection(db, "events"), 
              where("isoDate", ">=", start),
              where("isoDate", "<=", end),
              orderBy("isoDate", "asc")
            );
            const snap = await getDocs(q);
            const eventsList = snap.docs.map(d => d.data());

            if(eventsList.length === 0) {
                toast.error(`${exportYear}年のデータはありません`);
                return;
            }

            let csvContent = "\uFEFF"; 
            csvContent += "ID,日付,時間,タイトル,会場,参加費(円),参加費(P),定員FP,定員GK,参加人数,対戦相手,スコア(自),スコア(敵),MOM\n";

            eventsList.forEach(ev => {
                const membersCount = (ev.members || []).length;
                const row = [
                    ev.id || '',
                    ev.isoDate,
                    ev.time,
                    `"${ev.title}"`,
                    `"${ev.venue}"`,
                    ev.fee,
                    ev.feePoint || ev.fee,
                    ev.limitFP || 20,
                    ev.limitGK || 2,
                    membersCount,
                    `"${ev.opponentName || ''}"`,
                    ev.homeScore ?? '',
                    ev.awayScore ?? '',
                    `"${ev.mom || ''}"`
                ];
                csvContent += row.join(",") + "\n";
            });

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `events_${exportYear}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            console.error(e);
            toast.error("CSV出力に失敗しました");
        }
    };

    // --- UI Helpers ---
    
    const handleTypeSelect = (newType) => {
        setEventType(newType);
        const typeLabels = { training: '練習', match: '練習試合', official: '公式戦', event: 'イベント' };
        const label = typeLabels[newType];
        const cleanTitle = title.replace(/^【.*?】/, '');
        setTitle(`【${label}】${cleanTitle}`);
    };

    const toggleBelonging = (item) => {
        if (selectedBelongings.includes(item)) setSelectedBelongings(selectedBelongings.filter(i => i !== item));
        else setSelectedBelongings([...selectedBelongings, item]);
    };

    const toggleAllowedRank = (rankId) => {
        if (allowedRanks.includes(rankId)) setAllowedRanks(allowedRanks.filter(id => id !== rankId));
        else setAllowedRanks([...allowedRanks, rankId]);
    };

    const addMatchDetail = () => setMatchDetails([...matchDetails, { minute: '', scorer: '', scorerNo: '', assist: '', assistNo: '' }]);
    const removeMatchDetail = (index) => { const newDetails = [...matchDetails]; newDetails.splice(index, 1); setMatchDetails(newDetails); };
    const updateMatchDetail = (index, field, value) => { const newDetails = [...matchDetails]; newDetails[index][field] = value; setMatchDetails(newDetails); };
    const handleResetMatchResult = () => { if (!window.confirm("リセットしますか？")) return; setHomeScore(''); setAwayScore(''); setMom(''); setYoutubeUrl(''); setMatchDetails([]); };

    const getTileContent = ({ date, view }) => { if (view !== 'month') return null; const e = events.find(ev => ev.isoDate === formatDateToIso(date)); return e ? <div style={{color: e.status==='cancelled'?COLORS.danger:COLORS.primary, fontSize:'10px'}}>●</div> : null; };
    const getEventStatusText = (event) => {
        const fpC = (event.members||[]).filter(m => getMemberRole(m) === 'FP').length; const gkC = (event.members||[]).filter(m => getMemberRole(m) === 'GK').length;
        return `FP残${Math.max(0, event.limitFP - fpC)} / GK残${Math.max(0, event.limitGK - gkC)}`;
    };
    const getPenaltyLogs = (event) => {
        if(!event.cancelLogs || !event.cancelLogs.length) return [];
        if(!event.time) return [];
        try {
            const [year, month, day] = event.isoDate.split('-').map(Number);
            const [startTime] = event.time.split('-');
            const [h, m] = startTime.split(':').map(Number);
            const eventStart = new Date(year, month - 1, day, h, m);
            return event.cancelLogs.filter(log => {
                const cancelDate = new Date(log.canceledAt);
                const diffHours = (eventStart - cancelDate) / (1000 * 60 * 60);
                return diffHours <= 24;
            });
        } catch(e) { return []; }
    };

    const displayEvents = events.filter(e => e.isoDate === formatDateToIso(selectedDate));
    const editingEvent = events.find(e => e.id === editingId);
    const memberList = editingEvent ? (editingEvent.members || []).map(m => getMemberName(m)) : [];

    return (
        <>
            {/* CSV Export Area */}
            <div style={{marginBottom:'20px', padding:'15px', background:'#f0f4c3', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'space-between', border:'1px solid #dce775'}}>
                <div>
                    <span style={{fontWeight:'bold', marginRight:'10px', color:'#827717'}}>📊 イベントデータ出力</span>
                    <select value={exportYear} onChange={e=>setExportYear(e.target.value)} style={{padding:'5px'}}>
                        <option value="2024">2024年</option>
                        <option value="2025">2025年</option>
                        <option value="2026">2026年</option>
                        <option value="2027">2027年</option>
                    </select>
                </div>
                <button onClick={handleExportCSV} style={{background:'#827717', color:'white', border:'none', padding:'8px 15px', borderRadius:'4px', cursor:'pointer'}}>
                    CSVダウンロード
                </button>
            </div>

            <div className="card"><Calendar onChange={(d)=>{setSelectedDate(d); setDate(formatDateToIso(d));}} value={selectedDate} tileContent={getTileContent} locale="ja-JP" /></div>
            
            <div className="card" style={{ border: editingId ? `2px solid ${COLORS.primary}` : 'none' }}>
                <h2 style={{margin:'0 0 15px 0', fontSize:'18px'}}>{editingId ? '✏️ 編集' : '➕ 新規作成'}</h2>
                <form onSubmit={handleAddEvent}>
                    <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="イベントタイトル" style={{fontSize:'16px', fontWeight:'bold'}} />
                    
                    <div style={{margin:'15px 0'}}>
                        <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>種類</label>
                        <div style={{display:'flex', gap:'5px', marginTop:'5px'}}>
                            {['training','match','official','event'].map(type => (
                                <button type="button" key={type} onClick={()=>handleTypeSelect(type)} style={{flex:1, padding:'8px', border: eventType===type ? `2px solid ${COLORS.primary}` : '1px solid #ccc', background: eventType===type ? '#e3f2fd' : 'white', borderRadius:'8px', fontWeight: eventType===type ? 'bold':'normal', color: eventType===type ? COLORS.primary : '#666', fontSize:'12px', cursor:'pointer'}}>
                                    {type==='training'?'練習':(type==='match'?'練習試合':(type==='official'?'公式戦':'イベント'))}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'15px'}}>
                          <div>
                              <label style={{fontSize:'12px', color:'#666', fontWeight:'bold'}}>日付</label>
                              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                          </div>
                          <div>
                              <label style={{fontSize:'12px', color:'#666', fontWeight:'bold'}}>時間</label>
                              <div style={{display:'flex', alignItems:'center', gap:'5px'}}>
                                <select value={startHour} onChange={e=>setStartHour(e.target.value)} style={{padding:'5px'}}>{HOURS.map(h=><option key={h} value={h}>{h}</option>)}</select>:
                                <select value={startMin} onChange={e=>setStartMin(e.target.value)} style={{padding:'5px'}}>{MINUTES.map(m=><option key={m} value={m}>{m}</option>)}</select> ～ 
                                <select value={endHour} onChange={e=>setEndHour(e.target.value)} style={{padding:'5px'}}>{HOURS.map(h=><option key={h} value={h}>{h}</option>)}</select>:
                                <select value={endMin} onChange={e=>setEndMin(e.target.value)} style={{padding:'5px'}}>{MINUTES.map(m=><option key={m} value={m}>{m}</option>)}</select>
                             </div>
                          </div>
                    </div>

                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px', marginBottom:'15px'}}>
                        <div>
                            <label style={{fontSize:'12px', color:'#666', fontWeight:'bold'}}>参加費 (円)</label>
                            <input type="number" value={fee} onChange={e=>setFee(e.target.value)} placeholder="例: 1500" />
                        </div>
                        <div>
                            <label style={{fontSize:'12px', color:'#666', fontWeight:'bold'}}>ポイント価格 (任意)</label>
                            <input type="number" value={feePoint} onChange={e=>setFeePoint(e.target.value)} placeholder="空欄なら同額" />
                        </div>
                    </div>

                    {/* ▼▼▼ 追加: 割引適用のスイッチ ▼▼▼ */}
                    <div style={{marginBottom:'15px', padding:'10px', background: isOfficialEvent ? '#e3f2fd' : '#ffebee', borderRadius:'8px', border: isOfficialEvent ? '1px solid #90caf9' : '1px solid #ffcdd2'}}>
                        <label style={{display:'flex', alignItems:'center', cursor:'pointer', fontWeight:'bold', color: isOfficialEvent ? '#1565c0' : '#c62828'}}>
                            <input 
                                type="checkbox" 
                                checked={isOfficialEvent} 
                                onChange={e=>setIsOfficialEvent(e.target.checked)} 
                                style={{marginRight:'8px', transform:'scale(1.2)'}} 
                            />
                            {isOfficialEvent ? '👑 公式イベント (会員ランク適用)' : '🚫 特別イベント (割引なし・全員一律料金)'}
                        </label>
                    </div>
                    {/* ▲▲▲ 追加ここまで ▲▲▲ */}
                    
                    <div style={{marginBottom:'15px'}}>
                        <label style={{fontSize:'12px', color:'#666', fontWeight:'bold'}}>会場</label>
                        <div style={{display:'flex', alignItems:'center', gap:'5px', marginBottom:'5px'}}>
                            <select value={selectedVenueName} onChange={e=>{setSelectedVenueName(e.target.value); if(e.target.value!=='その他'){const v=venueOptions.find(o=>o.name===e.target.value); setVenue(v.name); setMapUrl(v.url);} else {setVenue('');}}} style={{flex:1}}>
                                {venueOptions.map(v=><option key={v.name} value={v.name}>{v.name}</option>)}<option value="その他">その他</option>
                            </select>
                            <button type="button" onClick={handleManageVenues} style={{background:'#666', color:'white', border:'none', borderRadius:'4px', padding:'4px 8px', fontSize:'12px', cursor:'pointer'}}>
                                ⚙️ 整理
                            </button>
                        </div>
                        {selectedVenueName === 'その他' && (
                            <div style={{padding:'10px', background:'#f5f5f5', borderRadius:'8px'}}>
                                <input type="text" value={venue} onChange={e=>setVenue(e.target.value)} placeholder="会場名を入力" style={{marginBottom:'5px'}} />
                                <input type="text" value={mapUrl} onChange={e=>setMapUrl(e.target.value)} placeholder="GoogleマップURL" />
                            </div>
                        )}
                    </div>

                    <div style={{marginBottom:'15px', background:'#e8f5e9', padding:'10px', borderRadius:'8px'}}>
                        <label style={{fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px'}}>⏳ 優先なし会員の受付開始</label>
                        <select value={visitorOpenDelay} onChange={e=>setVisitorOpenDelay(e.target.value)}>
                            <option value="0">即時 (全員同時)</option>
                            <option value="24">作成の24時間後から</option>
                            <option value="48">作成の48時間後から</option>
                            <option value="72">作成の3日後から</option>
                        </select>
                    </div>

                    <div style={{background:'#f9f9f9', padding:'10px', borderRadius:'8px', marginBottom:'15px'}}>
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'15px'}}>
                            <div><label style={{fontSize:'12px', fontWeight:'bold'}}>FP定員</label><input type="number" value={limitFP} onChange={e=>setLimitFP(e.target.value)} /></div>
                            <div><label style={{fontSize:'12px', fontWeight:'bold'}}>GK定員</label><input type="number" value={limitGK} onChange={e=>setLimitGK(e.target.value)} /></div>
                        </div>
                    </div>

                    <div style={{marginBottom:'15px', background:'#fffde7', padding:'10px', borderRadius:'8px', border:'1px solid #fff9c4'}}>
                        <label style={{fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px'}}>🚫 参加制限 (チェックしたランクは参加OK)</label>
                        <div style={{display:'flex', flexWrap:'wrap', gap:'10px'}}>
                            {ranks.map(r => <label key={r.id} style={{fontSize:'12px'}}><input type="checkbox" checked={allowedRanks.includes(r.id)} onChange={()=>toggleAllowedRank(r.id)} /> {r.icon} {r.name}</label>)}
                        </div>
                    </div>

                    <div style={{marginBottom:'15px', background:'#e1f5fe', padding:'10px', borderRadius:'8px', border:'1px solid #b3e5fc'}}>
                        <label style={{fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px', color:'#0277bd'}}>🎒 持ち物 (参加者に表示)</label>
                        <div style={{display:'flex', flexWrap:'wrap', gap:'10px'}}>
                            {belongingsMaster.length > 0 ? belongingsMaster.map((item, i) => (
                                <label key={i} style={{fontSize:'12px', cursor:'pointer', display:'flex', alignItems:'center'}}>
                                    <input type="checkbox" checked={selectedBelongings.includes(item)} onChange={()=>toggleBelonging(item)} style={{marginRight:'4px'}} />
                                    {item}
                                </label>
                            )) : <div style={{fontSize:'11px', color:'#666'}}>※設定タブで持ち物リストを登録してください</div>}
                        </div>
                    </div>

                    <div style={{marginBottom:'15px'}}>
                        <label style={{fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px'}}>⚠️ イベント詳細・注意喚起</label>
                        <textarea value={description} onChange={e=>setDescription(e.target.value)} rows="8" style={{width:'100%', fontSize:'14px', padding:'10px', borderRadius:'8px', border:'1px solid #ccc', resize:'vertical'}} />
                    </div>

                    <details style={{marginBottom:'15px', border:'1px solid #ddd', borderRadius:'8px', padding:'10px'}}>
                        <summary style={{fontSize:'14px', fontWeight:'bold', color:'#555', cursor:'pointer'}}>👕 ユニフォーム・対戦相手</summary>
                        <div style={{marginTop:'10px'}}>
                             <div style={{fontSize:'12px', fontWeight:'bold', marginBottom:'5px'}}>自チーム</div>
                             <div style={{display:'flex', gap:'10px', marginBottom:'10px'}}>
                                 <div style={{flex:1}}><label style={{fontSize:'10px'}}>FP</label>{!isCustomUniformFP ? <select value={myUniformFP} onChange={(e)=>{if(e.target.value==='custom'){setIsCustomUniformFP(true); setMyUniformFP('');}else{setMyUniformFP(e.target.value)}}}>{UNIFORM_PRESETS.FP.map(c=><option key={c} value={c}>{c}</option>)}<option value="custom">その他...</option></select> : <input type="text" value={myUniformFP} onChange={e=>setMyUniformFP(e.target.value)} placeholder="色" />}</div>
                                 <div style={{flex:1}}><label style={{fontSize:'10px'}}>GK</label>{!isCustomUniformGK ? <select value={myUniformGK} onChange={(e)=>{if(e.target.value==='custom'){setIsCustomUniformGK(true); setMyUniformGK('');}else{setMyUniformGK(e.target.value)}}}>{UNIFORM_PRESETS.GK.map(c=><option key={c} value={c}>{c}</option>)}<option value="custom">その他...</option></select> : <input type="text" value={myUniformGK} onChange={e=>setMyUniformGK(e.target.value)} placeholder="色" />}</div>
                             </div>
                             <div style={{fontSize:'12px', fontWeight:'bold', marginBottom:'5px'}}>対戦相手</div>
                             <input type="text" value={opponentName} onChange={e=>setOpponentName(e.target.value)} placeholder="チーム名" />
                             <div style={{display:'flex', gap:'10px', marginTop:'5px'}}>
                                 <input type="text" value={opponentUniformFP} onChange={e=>setOpponentUniformFP(e.target.value)} placeholder="FPユニ" style={{flex:1}} />
                                 <input type="text" value={opponentUniformGK} onChange={e=>setOpponentUniformGK(e.target.value)} placeholder="GKユニ" style={{flex:1}} />
                             </div>
                        </div>
                    </details>
                    
                    <details style={{marginBottom:'15px', border:'1px solid #ddd', borderRadius:'8px', padding:'10px'}}>
                        <summary style={{fontSize:'14px', fontWeight:'bold', color:'#555', cursor:'pointer'}}>⚙️ その他詳細設定</summary>
                        <div style={{marginTop:'10px'}}>
                             <label style={{fontSize:'12px', display:'block'}}>PayPayリンク</label>
                             <input type="text" value={paypayLink} onChange={e=>setPaypayLink(e.target.value)} placeholder="https://paypay.ne.jp/..." />
                             <label style={{fontSize:'12px', display:'block', marginTop:'10px'}}>チェックイン形式</label>
                             <select value={checkInType} onChange={(e)=>setCheckInType(e.target.value)}>
                                 <option value="team">チーム分け (練習・ゲーム用)</option>
                                 <option value="simple">到着報告のみ (公式戦・試合用)</option>
                             </select>
                        </div>
                    </details>
                    
                    <div style={{marginBottom:'20px', padding:'10px', background:'#f1f8e9', borderRadius:'8px', border:'1px solid #c5e1a5'}}>
                        <div style={{fontWeight:'bold', fontSize:'14px', marginBottom:'5px', color:'#33691e'}}>🔔 通知設定</div>
                        <div style={{display:'flex', gap:'20px'}}>
                            <label style={{fontSize:'13px', display:'flex', alignItems:'center', cursor:'pointer'}}>
                                <input 
                                    type="checkbox" 
                                    checked={sendLineNotification} 
                                    onChange={e=>setSendLineNotification(e.target.checked)} 
                                    style={{width:'18px', height:'18px', marginRight:'5px', accentColor:'#00B900'}} 
                                />
                                LINE通知
                            </label>
                            <label style={{fontSize:'13px', display:'flex', alignItems:'center', cursor:'pointer'}}>
                                <input 
                                    type="checkbox" 
                                    checked={sendPushNotification} 
                                    onChange={e=>setSendPushNotification(e.target.checked)} 
                                    style={{width:'18px', height:'18px', marginRight:'5px', accentColor:'#2196F3'}} 
                                />
                                アプリ通知 (Push)
                            </label>
                        </div>
                        <div style={{fontSize:'10px', color:'#666', marginTop:'5px'}}>
                            ※チェックを入れた方法でのみ、更新通知が送信されます。
                        </div>
                    </div>

                    <div style={{borderTop:'2px solid #eee', paddingTop:'15px', marginTop:'20px'}}>
                        <div onClick={()=>setIsResultFormOpen(!isResultFormOpen)} style={{fontSize:'16px', fontWeight:'bold', cursor:'pointer', color:'#333', background:'#f5f5f5', padding:'10px', borderRadius:'8px'}}>
                            {isResultFormOpen ? '▼' : '▶'} 📝 試合結果入力
                        </div>
                        {isResultFormOpen && (
                            <div style={{marginTop:'15px', padding:'10px', border:'1px solid #eee', borderRadius:'8px'}}>
                                <div style={{textAlign:'right', marginBottom:'10px'}}>
                                    <button type="button" onClick={handleResetMatchResult} className="btn btn-sm" style={{background:'#eee'}}>結果をクリア</button>
                                </div>
                                <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'10px', marginBottom:'10px'}}>
                                    <input type="number" value={homeScore} onChange={e=>setHomeScore(e.target.value)} placeholder="自" style={{width:'60px', fontSize:'24px', textAlign:'center', padding:'10px'}} />
                                    <span style={{fontSize:'20px'}}>-</span>
                                    <input type="number" value={awayScore} onChange={e=>setAwayScore(e.target.value)} placeholder="他" style={{width:'60px', fontSize:'24px', textAlign:'center', padding:'10px'}} />
                                </div>
                                <input type="text" value={youtubeUrl} onChange={e=>setYoutubeUrl(e.target.value)} placeholder="YouTube URL" />
                                
                                <div style={{marginBottom:'10px'}}>
                                    <label style={{fontSize:'12px', fontWeight:'bold'}}>🏅 MOM</label>
                                    <select value={mom} onChange={e=>setMom(e.target.value)}>
                                        <option value="">未選択</option>
                                        {memberList.map(m=><option key={m} value={m}>{m}</option>)}<option value="その他">その他</option>
                                    </select>
                                </div>

                                {editingId && (
                                    <div style={{marginTop:'20px', marginBottom:'20px', border:'2px solid #e65100', padding:'15px', borderRadius:'10px', background:'#fff3e0'}}>
                                        <h4 style={{marginTop:0, marginBottom:'10px', color:'#e65100'}}>🎁 イベント報酬</h4>
                                        <div style={{marginBottom:'15px'}}>
                                            <label style={{fontSize:'12px', display:'block', marginBottom:'5px'}}>🏅 MOMボーナス ({mom || '未選択'})</label>
                                            <div style={{display:'flex', gap:'5px'}}>
                                                <input type="number" value={momBonus} onChange={e=>setMomBonus(e.target.value)} style={{width:'80px', padding:'5px'}} />
                                                <button onClick={handleGiveMomBonus} type="button" className="btn btn-sm" style={{flex:1, background:'#ff9800', color:'white'}}>付与する</button>
                                            </div>
                                        </div>
                                        <div>
                                            <label style={{fontSize:'12px', display:'block', marginBottom:'5px'}}>🎁 参加者全員に還元 ({memberList.length}名)</label>
                                            <div style={{display:'flex', gap:'5px'}}>
                                                <input type="number" value={partBonus} onChange={e=>setPartBonus(e.target.value)} style={{width:'80px', padding:'5px'}} />
                                                <button onClick={handleGiveParticipationBonus} type="button" className="btn btn-sm" style={{flex:1, background:'#4caf50', color:'white'}}>一括付与</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div style={{background:'#f5f5f5', padding:'10px', borderRadius:'8px'}}>
                                    <label style={{fontWeight:'bold', fontSize:'12px', display:'block', marginBottom:'5px'}}>⚽ 得点詳細</label>
                                    {matchDetails.map((detail, idx) => (
                                        <div key={idx} style={{marginBottom:'10px', background:'white', borderRadius:'8px', padding:'10px', border:'1px solid #eee', marginTop:'5px', display:'flex', alignItems:'center', gap:'5px'}}>
                                            <input type="text" placeholder="分" value={detail.minute} onChange={(e)=>updateMatchDetail(idx, 'minute', e.target.value)} style={{width:'40px', padding:'5px', textAlign:'center'}} />
                                            <div style={{flex:1}}>
                                                <select value={detail.scorer} onChange={(e)=>updateMatchDetail(idx, 'scorer', e.target.value)} style={{width:'100%', padding:'5px', marginBottom:'2px', fontSize:'11px'}}>
                                                    <option value="">得点者</option>
                                                    {memberList.map(m => <option key={m} value={m}>{m}</option>)}<option value="オウンゴール">オウンゴール</option><option value="その他">その他</option>
                                                </select>
                                                <select value={detail.assist} onChange={(e)=>updateMatchDetail(idx, 'assist', e.target.value)} style={{width:'100%', padding:'5px', fontSize:'11px', color:'#666'}}>
                                                    <option value="">アシスト</option>
                                                    <option value="なし">なし</option>
                                                    {memberList.map(m => <option key={m} value={m}>{m}</option>)}<option value="その他">その他</option>
                                                </select>
                                            </div>
                                            <button type="button" onClick={()=>removeMatchDetail(idx)} style={{background:COLORS.danger, color:'white', border:'none', fontSize:'12px', width:'24px', height:'24px', borderRadius:'50%', cursor:'pointer'}}>×</button>
                                        </div>
                                    ))}
                                    <button type="button" onClick={addMatchDetail} className="btn btn-sm btn-success btn-full">+ 得点行を追加</button>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div style={{display:'flex', gap:'10px', marginTop:'20px'}}>
                        <button type="submit" className="btn btn-primary" style={{ flex:1, fontSize:'16px' }}>{editingId ? '更新して保存' : 'イベントを作成'}</button>
                        {editingId && <button type="button" onClick={resetFormExceptDate} className="btn" style={{background:'#999', color:'white'}}>キャンセル</button>}
                    </div>
                </form>
            </div>
            {displayEvents.map(event => {
                const members = event.members || [];
                const waitlist = event.waitlist || [];
                const penaltyLogs = getPenaltyLogs(event);

                let isPastEvent = false;
                try {
                    const now = new Date();
                    const [year, month, day] = event.isoDate.split('-').map(Number);
                    const [startTimeStr] = (event.time || '23:59').split('-');
                    const [startHour, startMin] = startTimeStr.split(':').map(Number);
                    const eventDate = new Date(year, month - 1, day, startHour, startMin);
                    if (eventDate < now) {
                        isPastEvent = true;
                    }
                } catch(e) {}

                return (
                <div key={event.id} className="card">
                    <div style={{background: event.status==='cancelled'?'#ef5350':'#333', color:'white', padding:'10px 15px', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'8px 8px 0 0', margin:'-20px -20px 15px -20px'}}>
                         <div style={{fontWeight:'bold'}}>{event.title}</div>
                         <div style={{fontSize:'11px'}}>{event.isoDate} {event.time}</div>
                    </div>
                    <div>
                         <div style={{display:'flex', justifyContent:'space-between', marginBottom:'10px'}}>
                             <div style={{fontSize:'12px', color:'#666'}}>📍 {event.venue}</div>
                             <div style={{fontSize:'12px', fontWeight:'bold', color:'#0056b3'}}>{getEventStatusText(event)}</div>
                         </div>
                         <div style={{marginBottom:'10px', border:'1px solid #eee', borderRadius:'8px', padding:'10px', background:'#fafafa'}}>
                             <div style={{fontSize:'11px', fontWeight:'bold', color:'#555', marginBottom:'5px'}}>✅ 参加メンバー</div>
                             <div style={{display:'flex', flexWrap:'wrap', gap:'5px'}}>
                                 {members.map((m, i) => {
                                     const name = getMemberName(m);
                                     const role = getMemberRole(m);
                                     const userId = typeof m === 'object' ? m.userId : null; 
                                     const userDetail = userId ? users.find(u => u.id === userId) : users.find(u => u.displayName === name);
                                     
                                     const userRankObj = userDetail ? getUserRank(userDetail, ranks) : { type: 'normal' };
                                     const isMonthlyMember = userDetail && (userDetail.isMonthly || userDetail.membership === 'monthly' || userRankObj.type === 'free');
                                     
                                     const isPaid = isPastEvent 
                                        ? (event.paidMembers && event.paidMembers.includes(name))
                                        : (userRankObj.type === 'free' || (event.paidMembers && event.paidMembers.includes(name)));
                                     
                                     const isCheckedIn = m.checkedInAt;

                                     return (
                                       <span key={i} style={{
                                            fontSize:'10px', padding:'4px 8px', borderRadius:'15px', 
                                            background: isPaid ? '#e8f5e9' : (isCheckedIn ? '#e3f2fd' : 'white'), 
                                            border: isCheckedIn ? `2px solid ${COLORS.primary}` : (isPaid ? `1px solid ${COLORS.success}` : '1px solid #ccc'),
                                            color:'#333', display:'flex', alignItems:'center',
                                            fontWeight: isCheckedIn ? 'bold' : 'normal'
                                       }}>
                                            {isCheckedIn && <span style={{marginRight:'3px', color:COLORS.primary}}>✅</span>}
                                            <span onClick={()=>togglePayment(event, name)} style={{cursor:'pointer', marginRight:'4px'}}>
                                                {role==='GK' && '🧤 '}{isMonthlyMember && '👑 '}{name} {isPaid && '💰'}
                                            </span>

                                            <button 
                                                onClick={()=>toggleCheckInAdmin(event, name)} 
                                                style={{
                                                    marginLeft:'4px', 
                                                    background: isCheckedIn ? '#ff9800' : COLORS.primary, 
                                                    color:'white', 
                                                    border:'none', 
                                                    borderRadius:'4px', 
                                                    padding:'2px 6px', 
                                                    cursor:'pointer', 
                                                    fontSize:'10px',
                                                    fontWeight:'bold'
                                                }}
                                                title="強制チェックイン / 取消"
                                            >
                                                📍
                                            </button>

                                            <span onClick={()=>handleForceRemoveMember(event, name, false)} style={{marginLeft:'8px', color:'#d32f2f', fontWeight:'bold', cursor:'pointer'}}>×</span>
                                       </span>
                                     );
                                 })}
                                 {members.length===0 && <span style={{fontSize:'10px', color:'#999'}}>なし</span>}
                             </div>
                         </div>
                         
                         {waitlist.length > 0 && (
                             <div style={{marginBottom:'10px', border:'1px dashed #ff9800', borderRadius:'8px', padding:'10px', background:'#fff3e0'}}>
                                 <div style={{fontSize:'11px', fontWeight:'bold', color:'#e65100', marginBottom:'5px'}}>⌛ キャンセル待ち</div>
                                 <div style={{display:'flex', flexWrap:'wrap', gap:'5px'}}>
                                     {waitlist.map((m, i) => {
                                        const name = getMemberName(m);
                                        return (
                                            <span key={i} style={{fontSize:'10px', padding:'2px 6px', borderRadius:'4px', background:'white', border:'1px solid #ffcc80', color:'#e65100', display:'flex', alignItems:'center'}}>
                                                {i+1}. {name}
                                                <span onClick={()=>handleForceRemoveMember(event, name, true)} style={{marginLeft:'4px', color:'#d32f2f', fontWeight:'bold', cursor:'pointer'}}>×</span>
                                            </span>
                                        );
                                     })}
                                 </div>
                             </div>
                         )}

                         {penaltyLogs.length > 0 && (
                             <div style={{marginTop:'10px', marginBottom:'10px', padding:'10px', background:'#ffebee', borderRadius:'8px', border:'1px solid #ffcdd2'}}>
                                <div style={{fontSize:'12px', fontWeight:'bold', color:'#c62828', marginBottom:'5px'}}>⚠️ 24時間以内のキャンセル</div>
                                {penaltyLogs.map((log, i) => (
                                   <div key={i} style={{fontSize:'11px', color:'#b71c1c', marginBottom:'2px'}}>
                                      ・{log.name} <span style={{fontSize:'9px'}}>({new Date(log.canceledAt).toLocaleString('ja-JP', {month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit'})})</span>
                                   </div>
                                ))}
                             </div>
                         )}
                         
                         <div style={{display:'flex', gap:'5px', justifyContent:'flex-end', marginTop:'10px', flexWrap:'wrap'}}>
                             <button onClick={()=>handleNoShowProcess(event)} className="btn btn-sm" style={{background:'#d32f2f', color:'white', fontWeight:'bold'}}>
                                 🚨 バックレ処理
                             </button>
                             <div style={{flex: 1}}></div>
                             <button onClick={()=>handleDuplicate(event)} className="btn btn-sm btn-success">コピー</button>
                             <button onClick={()=>handleEdit(event)} className="btn btn-sm btn-primary">編集</button>
                             <button onClick={()=>handleToggleStatus(event)} className="btn btn-sm" style={{background: event.status==='cancelled'?'#666':'#ff9800', color:'white'}}>
                                {event.status==='cancelled'?'再開':'中止'}
                             </button>
                             <button onClick={()=>handleDelete(event.id)} className="btn btn-sm btn-danger">削除</button>
                         </div>
                    </div>
                </div>
            )})}
            
            {/* CSV Export Area (Moved to Bottom & Styled) */}
            <div style={{marginTop:'30px', padding:'20px', background:'#f9f9f9', borderRadius:'8px', textAlign:'center', borderTop:'1px solid #ddd'}}>
                <div style={{fontSize:'12px', color:'#666', marginBottom:'10px', fontWeight:'bold'}}>📅 過去データの管理 (CSV出力)</div>
                <div style={{display:'flex', flexDirection:'column', gap:'10px', alignItems:'center'}}>
                    <select value={exportYear} onChange={e=>setExportYear(e.target.value)} style={{padding:'10px', borderRadius:'4px', border:'1px solid #ccc', width:'100%', maxWidth:'300px', fontSize:'14px'}}>
                        <option value="2024">2024年</option>
                        <option value="2025">2025年</option>
                        <option value="2026">2026年</option>
                        <option value="2027">2027年</option>
                    </select>
                    <button onClick={handleExportCSV} style={{background:'#827717', color:'white', border:'none', padding:'12px 20px', borderRadius:'4px', cursor:'pointer', width:'100%', maxWidth:'300px', fontWeight:'bold'}}>
                        CSVダウンロード
                    </button>
                </div>
            </div>
        </>
    );
};

export default AdminEvents;